// 定期募集・定期確定シフトの CSV アップロード（#906）。変換とチェックはバックエンド
// （staffCheckUploadScheduledShiftGroups）で行い、ここでは列の取り出しと、グループごとの実行だけを持つ。

// バックエンドの ScheduledShiftGroupCsvRecord と同じ列。GraphQL の input は知らない列を
// 受け付けないので、CSV にこれ以外の列があっても送らない
export const CSV_COLUMNS = [
  'groupKey',
  'name',
  'email',
  'startDate',
  'endDate',
  'dayOfWeek',
  'weeksOfMonth',
  'excludeHolidays',
  'departmentCode',
  'startTime',
  'splitDateTime1',
  'splitDateTime2',
  'splitDateTime3',
  'endTime',
  'splitHourlyWage1',
  'splitHourlyWage2',
  'splitHourlyWage3',
  'hourlyWage',
  'numberOfRooms',
  'comment',
  'isSplitDateTime1BreakTime',
  'isSplitDateTime2BreakTime',
  'isSplitDateTime3BreakTime',
  'isStartTimeBreakTime',
  'isPublished',
  'isDoubleRecruitment',
  'delete',
]

export const SCHEDULED_SHIFT_GROUP_CSV_LIMIT = 1000

// 一覧画面に入口のボタンを出す（お客様の STG 確認から表示。false にすると URL を直接開いたときだけ使える）
export const SHOW_SCHEDULED_SHIFT_GROUP_CSV_ENTRY = true

// Excel で保存した CSV は先頭に BOM が付き、1列目の見出しが「﻿groupKey」になる
export const normalizeCsvHeader = header =>
  String(header)
    .replace(/^﻿/, '')
    .trim()

export const toCsvRecords = rows =>
  rows.map(row =>
    CSV_COLUMNS.reduce((record, column) => {
      const value = row[column]
      return {
        ...record,
        [column]: value == null ? '' : String(value),
      }
    }, {})
  )

export const unknownCsvColumns = rows =>
  rows.length
    ? Object.keys(rows[0]).filter(key => !CSV_COLUMNS.includes(key))
    : []

export const GROUP_STATUS = {
  RUNNING: 'RUNNING',
  DONE: 'DONE',
  FAILED: 'FAILED',
}

// popup.error と同じ順で、画面に出す文言を取り出す
export const errorMessageOf = error => {
  if (error && error.networkError) return 'ネットワークエラーが発生しました。'
  if (error && error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0].message
  }
  return (error && error.message) || 'エラーが発生しました。'
}

const isRunnable = plan => plan.action === 'CREATE' || plan.action === 'DELETE'

export const countRunnableGroups = groups => groups.filter(isRunnable).length

/**
 * チェック結果のグループを1つずつ作成・削除する。削除を先に、それぞれ CSV の順に実行する
 * （削除して作り直すとき、古いグループの確定シフトと重ならないように。チェックも同じ前提で重なりを見る）。
 * 失敗したらそこで止める。statuses が DONE のグループは飛ばすので、もう一度呼ぶと続きから実行する。
 *
 * @param groups staffCheckUploadScheduledShiftGroups の groups
 * @param statuses { [groupKey]: { status, errorMessage } }（前回までの結果）
 * @param createGroup args => Promise（既存の作成 mutation）
 * @param deleteGroup existingGroupId => Promise（既存の削除 mutation）
 * @param onProgress statuses => void（1グループ進むごとに呼ぶ）
 * @returns { statuses, failed }
 */
export const runScheduledShiftGroupUpload = async ({
  groups,
  statuses = {},
  createGroup,
  deleteGroup,
  onProgress = () => {},
}) => {
  let current = { ...statuses }
  const update = (groupKey, value) => {
    current = { ...current, [groupKey]: value }
    onProgress(current)
  }

  const runnable = groups.filter(isRunnable)
  const ordered = [
    ...runnable.filter(plan => plan.action === 'DELETE'),
    ...runnable.filter(plan => plan.action === 'CREATE'),
  ]
  for (const plan of ordered) {
    const previous = current[plan.groupKey]
    if (previous && previous.status === GROUP_STATUS.DONE) continue
    update(plan.groupKey, { status: GROUP_STATUS.RUNNING })
    try {
      if (plan.action === 'CREATE') {
        // eslint-disable-next-line no-await-in-loop
        await createGroup(JSON.parse(plan.argsJson))
      } else {
        // eslint-disable-next-line no-await-in-loop
        await deleteGroup(plan.existingGroupId)
      }
      update(plan.groupKey, { status: GROUP_STATUS.DONE })
    } catch (error) {
      update(plan.groupKey, {
        status: GROUP_STATUS.FAILED,
        errorMessage: errorMessageOf(error),
      })
      return { statuses: current, failed: plan }
    }
  }
  return { statuses: current, failed: null }
}
